import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { accountApi } from '@portal/api/account/accountApi';
import { Account, AccountUpdatePayload, UpdatePasswordPayload, Designation } from '@portal/types/account.types';
import { useFlash } from '@portal/portal_registration/contexts/FlashContext';

/**
 * Hook to fetch the current account
 */
export const useAccount = () => {
  return useQuery({
    queryKey: ['account'],
    queryFn: () => accountApi.getAccount(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Don't retry on 401/403 errors
    throwOnError: false, // Don't throw errors, let components handle them
  });
};

/**
 * Hook to update the current account
 */
export const useUpdateAccount = () => {
  const queryClient = useQueryClient();
  const { setFlash } = useFlash();
  
  const showFlash = (message: string, type: 'success' | 'error' | 'info') => {
    setFlash({ message, type });
  };

  return useMutation({
    mutationFn: (payload: AccountUpdatePayload) => accountApi.updateAccount(payload),
    onSuccess: (data) => {
      // Update the cache with the new account data
      queryClient.setQueryData(['account'], data);
      showFlash('Profile updated successfully', 'success');
    },
    onError: (error: any) => {
      console.error('Update account error:', error);
      const message = error?.errors?.base?.[0] || error?.message || 'Failed to update profile';
      showFlash(message, 'error');
    }
  });
};

/**
 * Hook to update the account password
 */
export const useUpdatePassword = () => {
  const { setFlash } = useFlash();
  
  const showFlash = (message: string, type: 'success' | 'error' | 'info') => {
    setFlash({ message, type });
  };

  return useMutation({
    mutationFn: (payload: UpdatePasswordPayload) => accountApi.updatePassword(payload),
    onSuccess: (data) => {
      if (data.status === 'success') {
        showFlash(data.message || 'Password updated successfully', 'success');
      } else {
        showFlash(data.message || 'Failed to update password', 'error');
      }
    },
    onError: (error: any) => {
      console.error('Update password error:', error);
      
      // Parse different error formats
      let message = 'Failed to update password';
      
      if (error?.errors) {
        // Handle validation errors
        const errors = error.errors;
        if (errors.current_password) {
          message = `Current password ${errors.current_password[0]}`;
        } else if (errors.password) {
          message = `Password ${errors.password[0]}`;
        } else if (errors.password_confirmation) {
          message = `Password confirmation ${errors.password_confirmation[0]}`;
        } else if (errors.base) {
          message = errors.base[0];
        }
      } else if (error?.message) {
        message = error.message;
      }
      
      showFlash(message, 'error');
      
      // Re-throw to allow form-level error handling
      throw error;
    }
  });
};

/**
 * Hook to fetch available designations
 */
export const useDesignations = () => {
  return useQuery({
    queryKey: ['designations'],
    queryFn: () => accountApi.getDesignations(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};